import React, { FC, ReactNode, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { SelectedCompanyCurrency } from '../../../../components/selected-company-currency/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { StringKey } from '../../../../lang/index';
import { ExerciseEventItem, NewExerciseEvent } from '../../../../types/events.types';
import { SharePlanType } from '../../../../types/pool-plans.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { toNumber } from '../../../../utils/to-number.util';
import { EventAggregateDocumentsPreview } from './event-aggregate-documents-preview';
import {
  GrantSummaryPlainFields,
  GrantSummaryPlainRow,
  GrantSummarySection,
} from './grant-event-summary-layout';

const planTypeTranslationKey: Record<SharePlanType, StringKey> = {
  [SharePlanType.GROWTH_HURDLE]: 'HURDLE_APPROVAL',
  [SharePlanType.BSPCE]: 'EXERCISE_BSPCE',
  [SharePlanType.COMMON_STOCK]: 'EXERCISE',
  [SharePlanType.PHANTOM]: 'EXERCISE',
  [SharePlanType.RSA]: 'EXERCISE',
  [SharePlanType.RSU]: 'EXERCISE',
  [SharePlanType.STOCK_OPTIONS]: 'EXERCISE_STOCK_OPTIONS',
  [SharePlanType.SAR]: 'HURDLE_APPROVAL',
  [SharePlanType.WARRANTS]: 'EXERCISE_EXPIRE_WARRANTS',
};

type ExerciseEventEditPreviewSummaryProps = {
  documentsEditAction?: ReactNode;
  event: NewExerciseEvent;
  format: (date: Date, formatStr: string) => string;
  previewMode?: true;
  transactionId?: string;
};

export const ExerciseEventEditPreviewSummary: FC<ExerciseEventEditPreviewSummaryProps> = ({
  documentsEditAction,
  event,
  format,
  previewMode,
  transactionId,
}) => {
  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };

  const items = useMemo(() => {
    const list = event.exercise.items;
    return transactionId ? list.filter((i) => i.id === transactionId) : list;
  }, [event.exercise.items, transactionId]);

  const eventDisplayId = event.eventId.split('-')[0].toUpperCase();

  return (
    <div className="flex h-fit flex-col gap-4">
      {items.map((item) => (
        <ExerciseTransactionPreviewSection
          eventDisplayId={eventDisplayId}
          format={format}
          item={item}
          key={item.id}
        />
      ))}
      <ExerciseEventAggregateFooter
        additionalNotes={event.additionalNotes}
        documentsEditAction={documentsEditAction}
        event={event}
        filesLinks={event.filesLinks}
        handleDocumentClick={handleDocumentClick}
        previewMode={previewMode}
      />
    </div>
  );
};

type ExerciseTransactionPreviewSectionProps = {
  eventDisplayId: string;
  format: (date: Date, formatStr: string) => string;
  item: ExerciseEventItem;
};

const renderStrikeSummaryItem = (
  planType: SharePlanType,
  strikePrice: null | number | undefined,
  options?: { isLast?: boolean },
): ReactNode => {
  if (planType !== SharePlanType.STOCK_OPTIONS && planType !== SharePlanType.BSPCE) return null;

  return (
    <SummaryItem
      className={twMerge('border-b-[1px] border-gray-100', options?.isLast && 'pb-0')}
      title={<AppFormattedMessage id="STRIKE_PRICE" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={
        strikePrice != null ? (
          <>
            <SelectedCompanyCurrency />
            {toNumber(strikePrice).toLocaleString('en-US')}
          </>
        ) : (
          <span className="truncate text-xs font-[450] text-gray-700">-</span>
        )
      }
    />
  );
};

type ExerciseEventAggregateFooterProps = {
  additionalNotes: string;
  documentsEditAction?: ReactNode;
  event: NewExerciseEvent;
  filesLinks: string[];
  handleDocumentClick: (link: string) => void;
  previewMode?: true;
};

const ExerciseEventAggregateFooter: FC<ExerciseEventAggregateFooterProps> = ({
  event,
  filesLinks,
  additionalNotes,
  documentsEditAction,
  handleDocumentClick,
  previewMode,
}) => (
  <>
    <EventAggregateDocumentsPreview
      editAction={documentsEditAction}
      event={event}
      filesLinks={filesLinks}
      onDocumentClick={handleDocumentClick}
      previewMode={previewMode}
    />
    <GrantSummaryPlainFields>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <GrantSummaryPlainRow
            label={<AppFormattedMessage id="ADDITIONAL_NOTES" />}
            value={
              <span
                className={twMerge(
                  'max-w-[250px] break-words',
                  !previewMode ? 'max-w-[220px]' : 'max-w-[250px]',
                )}
              >
                {additionalNotes || '-'}
              </span>
            }
          />
        </div>
        {documentsEditAction}
      </div>
    </GrantSummaryPlainFields>
  </>
);

const ExerciseTransactionPreviewSection: FC<ExerciseTransactionPreviewSectionProps> = ({
  item,
  format,
  eventDisplayId,
}) => {
  const stakeholderName = item.vestingTask.grantItem.stakeholder.fullName;
  const planType = item.vestingTask.grantItem.plan.type;
  const strikePrice = item.vestingTask.grantItem.plan.strikePrice;

  const title = (
    <Tooltip childrenWrapperClassName="max-w-[380px]" content={stakeholderName}>
      <span className="truncate">
        <AppFormattedMessage id="EXERCISE" /> - {stakeholderName}
      </span>
    </Tooltip>
  );

  const grantIdShort = item.vestingTask.grantItem.id.split('-')[0];
  const shareClassName = item.vestingTask.grantItem.plan.pool.shareClass.name;

  const transactionTypeRow = (
    <SummaryItem
      className="border-b-[1px] border-gray-100"
      title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={<AppFormattedMessage id={planTypeTranslationKey[planType]} />}
    />
  );

  const stakeholderRow = (
    <SummaryItem
      className="border-b-[1px] border-gray-100"
      title={<AppFormattedMessage id="STAKEHOLDER" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={
        <Tooltip content={stakeholderName}>
          <span className="truncate">{stakeholderName}</span>
        </Tooltip>
      }
    />
  );

  const grantIdRow = (
    <SummaryItem
      className="border-b-[1px] border-gray-100"
      title={<AppFormattedMessage id="GRANT_ID" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={<span className="uppercase">{grantIdShort}</span>}
    />
  );

  const shareClassRow = (
    <SummaryItem
      className="border-b-[1px] border-gray-100"
      title={<AppFormattedMessage id="SHARE_CLASS" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={
        <Tooltip content={shareClassName}>
          <span className="truncate">{shareClassName}</span>
        </Tooltip>
      }
    />
  );

  const dateRow = (
    <SummaryItem
      className="border-b-[1px] border-gray-100 pt-0"
      title={<AppFormattedMessage id="DATE" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={format(item.date, 'dd/MM/yyyy')}
    />
  );

  const eventIdRow = (
    <SummaryItem
      className="border-b-[1px] border-gray-100"
      title={<AppFormattedMessage id="EVENT_ID" />}
      titleClassName="w-fit"
      titleWrapperClassName="w-[120px]"
      value={<span className="uppercase">{eventDisplayId}</span>}
    />
  );

  let body: ReactNode;

  if (planType === SharePlanType.GROWTH_HURDLE) {
    body = (
      <>
        {dateRow}
        {eventIdRow}
        {transactionTypeRow}
        {stakeholderRow}
        {grantIdRow}
        <SummaryItem
          className="border-b-[1px] border-gray-100"
          title={<AppFormattedMessage id="EXERCISED_GRANTS" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={toNumber(item.exercised).toLocaleString('en-US')}
        />
        {shareClassRow}
        <SummaryItem
          className="pb-0"
          title={<AppFormattedMessage id="ISSUED_SHARES" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={item.vestingTask.grantItem.numbersOfGrants.toLocaleString('en-US')}
        />
      </>
    );
  } else if (planType === SharePlanType.STOCK_OPTIONS || planType === SharePlanType.BSPCE) {
    const issued =
      toNumber(item.exercised) * toNumber(item.vestingTask?.grantItem?.plan?.conversionRatio || 1);
    body = (
      <>
        {dateRow}
        {eventIdRow}
        {transactionTypeRow}
        {stakeholderRow}
        {grantIdRow}
        <SummaryItem
          className="border-b-[1px] border-gray-100"
          title={<AppFormattedMessage id="EXERCISED_OPTIONS" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={toNumber(item.exercised).toLocaleString('en-US')}
        />
        {shareClassRow}
        {renderStrikeSummaryItem(planType, strikePrice)}
        <SummaryItem
          className="pb-0"
          title={<AppFormattedMessage id="ISSUED_SHARES" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={issued.toLocaleString('en-US')}
        />
      </>
    );
  } else if (planType === SharePlanType.WARRANTS) {
    const exercisedWarrants =
      item.exercised / toNumber(item.vestingTask.grantItem.plan.conversionRatio, 1);
    body = (
      <>
        {dateRow}
        {eventIdRow}
        {transactionTypeRow}
        {stakeholderRow}
        {grantIdRow}
        <SummaryItem
          className="border-b-[1px] border-gray-100"
          title={<AppFormattedMessage id="EXERCISED_WARRANTS" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={exercisedWarrants.toLocaleString('en-US')}
        />
        {shareClassRow}
        <SummaryItem
          className="pb-0"
          title={<AppFormattedMessage id="ISSUED_SHARES" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={item.vestingTask.grantItem.numbersOfGrants.toLocaleString('en-US')}
        />
      </>
    );
  } else if (planType === SharePlanType.SAR) {
    body = (
      <>
        {dateRow}
        {eventIdRow}
        {transactionTypeRow}
        {stakeholderRow}
        {grantIdRow}
        <SummaryItem
          className="border-b-[1px] border-gray-100"
          title={<AppFormattedMessage id="SAR_EXERCISED" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={item.exercised.toLocaleString('en-US')}
        />
        {shareClassRow}
        <SummaryItem
          className="border-b-[1px] border-gray-100"
          title={<AppFormattedMessage id="SAR_PAYOUT" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={
            <>
              <SelectedCompanyCurrency />
              {item.payout != null ? item.payout.toLocaleString('en-US') : '—'}
            </>
          }
        />
        <SummaryItem
          className="pb-0"
          title={<AppFormattedMessage id="ISSUED_SHARES" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={item.exercised.toLocaleString('en-US')}
        />
      </>
    );
  } else {
    const strikeForElse = renderStrikeSummaryItem(planType, strikePrice, { isLast: true });
    body = (
      <>
        {dateRow}
        {eventIdRow}
        {transactionTypeRow}
        {stakeholderRow}
        {grantIdRow}
        <SummaryItem
          className="border-b-[1px] border-gray-100"
          title={<AppFormattedMessage id="EXERCISED_GRANTS" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={toNumber(item.exercised).toLocaleString('en-US')}
        />
        <SummaryItem
          className={twMerge('border-b-[1px] border-gray-100', !strikeForElse && 'pb-0')}
          title={<AppFormattedMessage id="SHARE_CLASS" />}
          titleClassName="w-fit"
          titleWrapperClassName="w-[120px]"
          value={
            <Tooltip content={shareClassName}>
              <span className="truncate">{shareClassName}</span>
            </Tooltip>
          }
        />
        {strikeForElse}
      </>
    );
  }

  return <GrantSummarySection title={title}>{body}</GrantSummarySection>;
};
